import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check so AuthCallback can exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // 2FA challenge — caller handles the verify step
    if (data?.requires_2fa) {
      return {
        requires_2fa: true,
        challenge_id: data.challenge_id,
        phone_hint: data.phone_hint || "",
        email_hint: data.email_hint || "",
        sms_sent: !!data.sms_sent,
        email_sent: !!data.email_sent,
        debug_code: data.debug_code,
      };
    }
    // The backend set an httpOnly `session_token` cookie — no need to
    // persist the JWT in JS-readable storage.
    setUser(data.user);
    return data.user;
  };

  const verify2fa = async (challenge_id, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_id, code });
    // Master admin YubiKey gate — layered AFTER email 2FA. Login flow needs
    // one more step (WebAuthn get() or master fallback password).
    if (data?.requires_yubikey) {
      return {
        requires_yubikey: true,
        yubikey_challenge_id: data.yubikey_challenge_id,
        publicKey: data.publicKey,
      };
    }
    setUser(data.user);
    return data.user;
  };

  const finishYubikey = async (challenge_id, credential) => {
    const { data } = await api.post(
      "/security/yubikey/authenticate/finish",
      { challenge_id, credential },
    );
    setUser(data.user);
    return data.user;
  };

  const yubikeyFallback = async (challenge_id, password) => {
    const { data } = await api.post(
      "/security/yubikey/authenticate/fallback",
      { challenge_id, password },
    );
    setUser(data.user);
    return data.user;
  };

  const reauth = async (password) => {
    const { data } = await api.post("/auth/reauth", { password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // We're logging out anyway — `logger.debug` is auto-suppressed in prod
      logger.debug("logout call failed (ignored):", err?.message);
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{
      user, setUser, loading, login, verify2fa,
      finishYubikey, yubikeyFallback, reauth, logout, checkAuth,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
